import { Injectable, Injector } from '@angular/core';

import { FormField } from '@services';
import { IObjectStringKeyMap } from '@shared/models';
import { OutputValue } from '@shared/components/compare-value/model';
import { Asset, ReportAsset } from '@services/assets/asset.interfaces';
import { MeasurePickerComponent, SelectFiltersFormComponent } from '@modules/reporting/components';
import { BaseField, ReportClass } from '@modules/reporting/models/types/base';
import { PropertySubject } from '@services/property/property-model.interfaces';
import { AssetReportBaseClass } from '@modules/reporting/models/types/asset/assetBase';
import { AssetSummaryModelService } from '@modules/reporting/models/types/asset/service';
import { FolderDataType } from '@modules/management/modules/folders/model/folders.interfaces';
import { AssetStatusService } from '@modules/observation/pages/asset-status/pages/detail/services/asset-status.service';
import {
  AssetStateHistoryEntity,
  StateHistoryPeriodValue
} from '@modules/observation/pages/asset-status/pages/detail/model/asset-state.interfaces';

import {
  cloneDeep,
  each,
  filter,
  find,
  flatten,
  forEach,
  get,
  has,
  indexOf,
  isArray,
  keys,
  map,
  merge,
  orderBy,
  values,
  difference,
  unionBy,
  clone,
  last,
  includes,
  isObjectLike,
  split,
  some,
  isEmpty,
  pick
} from 'lodash';

@Injectable()
export class AssetSummaryReport extends AssetReportBaseClass {
  public reportType = ReportClass.AssetSummary;

  private assetSummaryModelService: AssetSummaryModelService = this.injector.get(AssetSummaryModelService);
  private assetStatusService: AssetStatusService = this.injector.get(AssetStatusService);
  public reportColumnOptions = this.assetSummaryModelService.getColumns();
  public reportFieldOptions = this.assetSummaryModelService.getFieldOptions();

  public filtersFields = [
    this.baseFields[BaseField.Locations],
    Object.assign({}, this.baseFields[BaseField.TargetAssets], {
      title: this.translate.instant('REPORTING.Asset_Or_Folder')
    }),
    // Object.assign({}, this.baseFields[BaseField.TargetContent], { TODO this field will be moved to a new content report
    //   title: this.translate.instant('REPORTING.Content_Item_Or_Folder')
    // }),
    {
      containerClass: 'report-field',
      title: this.translate.instant('SHARED.Zones'),
      name: 'zones',
      type: 'selectmenu',
      multiple: true,
      canDelete: true,
      placeholder: this.translate.instant('SHARED.Any_Zone'),
      options: this.zones,
      onRemoveField: (field: any) => this.onRemoveField(field),
      func: (ref) => this.showDeactivated(ref.id, ref)
    },
    {
      containerClass: 'report-field',
      title: this.translate.instant('MGMT_DETAILS.Go_Tos'),
      name: 'resources',
      type: 'selectmenu',
      multiple: true,
      canDelete: true,
      placeholder: this.translate.instant('SHARED.Any_User'),
      valueProperty: 'userID',
      options: this.accounts,
      onRemoveField: (field: any) => this.onRemoveField(field),
      func: (ref) => this.showDeactivated(this.userService.getFullname(ref.userID), ref)
    },
    {
      containerClass: 'report-field',
      title: this.translate.instant('SHARED.Active'),
      name: 'active',
      type: 'selectmenu',
      role: 'none',
      multiple: false,
      canDelete: true,
      placeholder: this.translate.instant('SHARED.All'),
      options: [
        {
          id: 1,
          description: this.translate.instant('SHARED.Yes')
        },
        {
          id: 0,
          description: this.translate.instant('SHARED.No')
        }
      ],
      onRemoveField: (field: any) => this.onRemoveField(field)
    },
    {
      containerClass: 'report-field',
      title: this.translate.instant('REPORTING.Asset_Status_Value'),
      name: 'statusValue',
      type: 'selectmenu',
      role: 'none',
      multiple: true,
      canDelete: true,
      placeholder: this.translate.instant('SHARED.All'),
      options: [
        {
          id: 2,
          description: this.translate.instant('PROPERTY.State_Up')
        },
        {
          id: 1,
          description: this.translate.instant('PROPERTY.State_Strained')
        },
        {
          id: 0,
          description: this.translate.instant('PROPERTY.State_Down')
        }
      ],
      onRemoveField: (field: any) => this.onRemoveField(field)
    },
    ...this.getTimeFilterFields(),
    ...this.getAssetDynamicNumericPropertyFields()
  ];
  public fields: FormField[] = [
    {
      containerClass: 'report-field obsdets',
      title: this.translate.instant('REPORTING.EDIT_title2'),
      name: 'assetSummaryPrimaryField',
      type: 'selectmenu',
      multiple: false,
      required: true,
      originalOrder: false,
      options: cloneDeep(this.reportFieldOptions),
      hideFieldsOnValue: {
        fieldNames: ['assetSummarySecondaryField', 'timespan', 'period', 'reportColumns', 'graphType'],
        value: 'none'
      },
      onChange: () => this.updateGraphFields('secondary')
    },
    {
      containerClass: 'report-field obsdets',
      title: this.translate.instant('REPORTING.EDIT_title3'),
      name: 'assetSummarySecondaryField',
      canClear: true,
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.None'),
      multiple: false,
      originalOrder: false,
      required: false,
      options: this.reportFieldOptions,
      onChange: () => this.updateGraphFields('primary')
    },
    this.baseFields[BaseField.Timespan],
    this.baseFields[BaseField.LocationTime],
    this.baseFields[BaseField.Period],
    {
      containerClass: 'report-field obsdets',
      title: this.translate.instant('REPORTING.EDIT_Data_to_Include'),
      name: 'assetSummaryReportColumns',
      // CVX-6554: pick a measure, then its treatments; the value is still a flat list of column ids
      type: 'customElement',
      component: MeasurePickerComponent,
      required: true,
      inputs: {options: this.reportColumnOptions},
      outputs: {
        onChanged: (values: string[]) => this.syncGraphSecondaryField('assetSummaryReportColumns', 'assetSummaryGraphSecondary', values)
      },
      options: this.reportColumnOptions
    },
    this.baseFields[BaseField.GraphType],
    {
      containerClass: 'report-field obsdets graphing',
      title: this.translate.instant('REPORTING.EDIT_Graph_by'),
      name: 'assetSummaryGraphPrimary',
      type: 'selectmenu',
      multiple: false,
      required: false,
      options: unionBy(this.reportFieldOptions, <any>[
        {id: 'period', description: this.translate.instant('SHARED.Period')},
        // { id: 'timespan', description: "Timespan" }
      ], 'id')
    },
    {
      containerClass: 'report-field obsdets graphing',
      title: this.translate.instant('REPORTING.EDIT_Include_in_graph'),
      name: 'assetSummaryGraphSecondary',
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.None'),
      multiple: true,
      required: false,
      options: this.reportColumnOptions
    },
    this.baseFields[BaseField.IncludingAll],
    this.baseFields[BaseField.ShowDataLabels],
    {
      containerClass: 'report-field obsdets ccsFormSubdivider',
      title: this.translate.instant('REPORTING.EDIT_Report_Data_Selection'),
      type: 'divider',
    },
    {
      containerClass: 'custom-data-field report-field',
      title: this.translate.instant('SHARED.Select_Filters'),
      name: 'filtersFields',
      type: 'customElement',
      component: SelectFiltersFormComponent,
      inputs: {
        options: cloneDeep(this.filtersFields),
        selectedItems: []
      },
      outputs: {
        onChanged: (selectedItems) => {
          const field = <FormField>find(this.formConfig.fields, {name: 'filtersFields'});
          (field.componentRef as SelectFiltersFormComponent).selectedItems = selectedItems;
          this.removedFilters = difference(this.filters, selectedItems);
          this.filters = selectedItems;
          this.showFilters();
        }
      },
      options: cloneDeep(this.filtersFields)
    },
  ];

  constructor(protected injector: Injector) {
    super(injector);
  }

  public init(reportData) {
    super.init(reportData);
    this.syncGraphSecondaryField('assetSummaryReportColumns', 'assetSummaryGraphSecondary');
  }

  public async report(theTable, theChart, report, reportId?: number) {
    this.reportData = report;
    // try {
    const opts = cloneDeep(report.selectors);
    const primaryGroup: number = get(this.accountsService.getAccount(this.userdataService.userID), 'primaryGroup');

    if (primaryGroup) {
      opts.groups = map(opts.groups, (group: number) => group === -1 ? primaryGroup : group);
    }

    const timeByTimespan = this.getTimeByTimespan(opts.timespan, opts.startTime, opts.endTime);
    opts.startTime = timeByTimespan.startTime;
    opts.endTime = timeByTimespan.endTime;
    // opts.includingAll = !opts.hasOwnProperty('locations') || opts.locations.length === 0 ? opts.includingAll : '';
    if (!opts.hasOwnProperty('locations') || opts.locations.length === 0) {
      opts.locations = this.userdataService.locations;
    }

    // prune out meaninless options - empty arrays are the same
    // as "all"

    each(keys(opts), item => {
      const rItem = opts[item];
      if (isArray(rItem)) {
        if (!rItem.length) {
          delete opts[item];
        }
      }
    });

    const r = await this.findInterval(opts);
    const tableDef = merge({}, this.tableDef);

    // create a table using the primary (and possibly secondary columns)
    const pRef: any = find(this.reportFieldOptions, {id: opts.primary});
    const sRef: any = find(this.reportFieldOptions, {id: opts.secondary});

    if (pRef) {
      tableDef.columns.push({
        id: pRef.id,
        title: pRef.label,
        fromID: pRef.fieldName,
        headerClass: 'text-left',
        class: 'tableexport-string'
      });
    }
    if (sRef) {
      tableDef.columns.push({
        id: sRef.id,
        title: sRef.label,
        fromID: `secondary_${sRef.fieldName}`,
        headerClass: 'text-left',
        class: 'tableexport-string'
      });
      tableDef.groupBy = 0;
    }
    let counter = 0;

    // create columns for each period

    const totalGroup = opts.period === 'none' ? null : 'total';
    if (totalGroup) {
      tableDef.colgroups[totalGroup] = {title: 'TOTAL'};
    }

    if (opts.period !== 'none') {
      forEach(r.intervals, (ref) => {
        const item = 'p' + counter;
        if (opts.extraColumns) {
          tableDef.colgroups[item] = {title: ref.label};
          each(opts.extraColumns, (colname, index: number) => {
            const column = find(this.reportColumnOptions, {id: colname});
            tableDef.columns.push(this.addColumn(column, index, item + colname, ref.label));
          });
        }
        counter++;
      });
    }

    // need columns for the totals of any extra columns
    if (opts.extraColumns) {
      each(opts.extraColumns, (colname, index: number) => {
        const column = find(this.reportColumnOptions, {id: colname});
        tableDef.columns.push(this.addColumn(column, index, 'total' + colname, '', true));
      });
    }


    // okay - iterate over the returned data, sorting by primary and possibly secondary key
    let rowRefs = [];
    // first, order the primary labels
    const rows = flatten(values(this.filterCategories(r.rows, opts)));
    const pOrder = this.utils.sortArray(rows, [
      {
        name: 'label',
        function: (item) => {
          if (item && typeof item === 'string') {
            return item.toLowerCase();
          } else {
            return item;
          }
        }
      }]
    );
    const significantPrimaryKeys = [];
    const significantSecondaryKeys = [];
    if (sRef) {
      // we have a secondary key
      each(pOrder, (pitem: any, pds) => {
        let significant = false;
        const sOrder = this.utils.sortArray(map(pitem.secondary), [{
          name: 'label',
          function: (item) => {
            if (item && typeof item === 'string') {
              return item.toLowerCase();
            } else {
              return item;
            }
          }
        }]);
        each(sOrder, (item, sds) => {
          if (item.items.length || opts.includingAll) {
            significant = true;
            if (indexOf(significantSecondaryKeys, item.label) < 0) {
              significantSecondaryKeys.push(item.label);
            }
            // there were matching items
            // push this data into a row
            const d = item;
            d.totalObservations = item.items.length;
            each(item.extras, (value, col) => {
              d['total' + col] = value;
            });
            each(item.intervals, (iref, name) => {
              d[name] = iref.items.length;
              each(iref.extras, (value, col) => {
                d[name + col] = value;
              });
            });
            rowRefs.push(d);
          }
        });
        if (significant) {
          if (indexOf(significantPrimaryKeys, pitem.label) < 0) {
            significantPrimaryKeys.push(pitem.label);
          }
        }
      });
    } else {
      // we only have a primary key
      each(pOrder, (item: any, pds) => {
        if (item.items.length || opts.includingAll) {
          if (indexOf(significantPrimaryKeys, item.label) < 0) {
            significantPrimaryKeys.push(item.label);
          }
          // push this data into a row
          const d = item;
          d.totalObservations = d.items.length;
          each(item.extras, (value, col) => {
            d['total' + col] = value;

          });
          each(item.intervals, (iref, name) => {
            d[name] = iref.items.length;
            each(iref.extras, (value, col) => {
              d[name + col] = value;
            });
          });
          rowRefs.push(d);
        }
      });
    }

    if (theChart) {
      // okay all the rows are gathered.  Do we need to graph?
      if (opts.graphType !== 'none') {
        const reportFieldOptions = clone(this.reportFieldOptions);
        const reportColumnOptions = clone(this.reportColumnOptions);
        const keys = {
          primary: significantPrimaryKeys,
          secondary: significantSecondaryKeys
        };

        const graphData: any = this.graphService.graphdataByColumns(opts, reportColumnOptions, (columnOpts) => {
          if (columnOpts.graphPrimary === 'period') {
            return this.graphService.graphdataByPeriod(columnOpts, r, rowRefs, keys, reportFieldOptions, reportColumnOptions);
          } else if (columnOpts.graphPrimary === columnOpts.primary) {
            return this.graphService.graphdataByPrimary(columnOpts, rowRefs, keys, reportFieldOptions, reportColumnOptions);
          } else if (columnOpts.secondary && columnOpts.graphPrimary === columnOpts.secondary) {
            return this.graphService.graphdataBySecondary(columnOpts, rowRefs, keys, reportFieldOptions, reportColumnOptions);
          }
        });
        if (graphData) {
          $('.report-view-has-chart').show();
          this.drawReportGraph(theChart, graphData, opts, reportId);
        }
      } else {
        $('.report-view-has-chart').hide();
      }
    }
    if (theTable) {
      rowRefs = orderBy(rowRefs, 'label', ['asc', 'desc']);
      tableDef.data = rowRefs;
      tableDef.columns = this.preparationColumns(tableDef.columns);
      this.tableService.showDataTable(theTable, tableDef);
    }
  }

  public async findInterval(opts) {
    const intervals: any[] = this.observations.calendarInterval(opts.startTime, opts.endTime, opts.period, opts.timespan, opts.format);
    if (opts?.targetAssets?.length) {
      opts.assetFolderTree = this.foldersDataService.getFolderTreeBy(FolderDataType.ASSET);
    }
    // CVX-6545: fetch events only for the targeted assets (isValid re-checks every filter below)
    const targets = pick(opts, ['targetAssets', 'assetFolderTree']);
    let assets = this.assetsService.getEnabledAssets().filter((asset) => this.assetSummaryModelService.isValid(asset, targets));
    assets = await this.assetsService.getDetailedAssets(map(assets, 'assetID'), opts.startTime);

    let primary: IObjectStringKeyMap<any> = {};
    const primaryKeys = {};
    const secondaryKeys = {};

    const pRef: any = find(this.reportFieldOptions, {id: opts.primary});
    const sRef: any = find(this.reportFieldOptions, {id: opts.secondary});

    primary = this.getPrimary(opts, sRef, intervals);

    opts.intervals = {
      allTime: {
        start: opts.startTime,
        end: opts.endTime,
        timezones: {}
      }
    };

    forEach(assets, (asset) => {
      const ctime = asset.created * 1000;

      // if (this.assetSummaryModelService.outOfRange(opts, asset, ctime, opts.intervals.allTime)) {
      //   return;
      // }

      if (!this.assetSummaryModelService.isValid(asset, opts)) {
        return;
      }

      // Apply time-based filters - build healthEntities for full time range first
      const reportAsset = asset as ReportAsset;
      reportAsset.healthEntities = this.assetStatusService.buildStates(
        reportAsset.healthEvents,
        opts.timespan,
        { startTime: opts.startTime, endTime: opts.endTime },
        true
      );
      if (!this.assetSummaryModelService.passesTimeFilters(reportAsset, opts)) {
        return;
      }

      const createItem = (key: string, label: string) => {
        const newItem: any = {
          extras: {},
          items: [],
          label,
          intervals: {},
          [pRef.fieldName]: label,
          fieldName: pRef.fieldName
        };

        primaryKeys[label] = key;

        if (sRef) {
          newItem.secondary = {};
        } else {
          if (opts.interval !== 'none') {
            each(intervals, (iref, counter) => {
              const iname = 'p' + counter;
              newItem.intervals[iname] = {extras: {}, items: []};
            });
          }
        }

        return newItem;
      };

      const findItem = (key: string, label: string) => {
        if (!has(primary, key)) {
          primary[key] = createItem(key, label);
        } else {
          const item = find(flatten([primary[key]]), <any>{label});

          if (!item) {
            primary[key] = [primary[key], createItem(key, label)];
          }
        }

        return find(flatten([primary[key]]), <any>{label});
      };

      // CVX-6545: an asset's per-interval view is the same for every row it lands in, so build it once
      let intervalAssets: ReportAsset[];

      // function to accumulate an individual item
      const addItem = (p, s) => {
        if (p[0] !== undefined) {
          let target = null;
          // we have a key
          const item: any = findItem(p[0], p[1]);
          if (sRef) {
            if (s[0] !== undefined) {
              // we have a key
              if (!has(secondaryKeys, s[1])) {
                // remember it
                secondaryKeys[s[1]] = s[0];
              }

              if (!has(item.secondary, s[0])) {
                // we don't have this bucket yet
                item.secondary[s[0]] = {
                  extras: {},
                  items: [],
                  label: s[1],
                  intervals: {},
                  fieldName: pRef.fieldName,
                  secondFieldName: sRef.fieldName,
                  [pRef.fieldName]: p[1],
                  [`secondary_${sRef.fieldName}`]: s[1]
                };
                // add in the interval buckets too
                if (opts.interval !== 'none') {
                  each(intervals, (iref, counter) => {
                    const iname = 'p' + counter;
                    item.secondary[s[0]].intervals[iname] = {extras: {}, items: []};
                  });
                }
              }
              item.secondary[s[0]].items.push(asset);
              target = item.secondary[s[0]];
            }
          } else {
            item.items.push(asset);
            target = item;
          }

          if (opts.interval !== 'none') {
            intervalAssets ??= map(intervals, (iref) => {
              const assetRef = AssetSummaryReport.eventCopy(asset);
              this.filterAsset(assetRef, iref, opts);
              return assetRef;
            });
            forEach(intervalAssets, (assetRef, counter) => {
              target.intervals['p' + counter].items.push(assetRef);
            });
          }
        }
      };

      // hold the primary key and label
      // get the primary field value from the record
      let pvList = get(asset, pRef.fieldName);
      if (pvList === undefined) {
        if (pRef.fieldRequired) {
          pvList = null;
        }
      }
      if (!isArray(pvList)) {
        pvList = [pvList];
      }
      each(pvList, (pv) => {
        let p = [];
        if (pv == null) {
          return;
        }
        if (pRef.hasOwnProperty('fieldFunc')) {
          // use the field function to derive the key and label
          // vrom the primary value
          p = pRef.fieldFunc(pv, asset);
        } else {
          // there is no translation function.  let's just use the id as
          // the key and the value
          p = [pv, pv];
        }
        if (sRef) {
          // hold the primary key and label
          let s = [];
          // get the primary field value from the record
          let svList = get(asset, sRef.fieldName);
          if (svList === undefined) {
            if (sRef.fieldRequired) {
              svList = null;
            }
          }
          if (!isArray(svList)) {
            svList = [svList];
          }
          each(svList, (sv) => {
            if (sv == null) {
              return;
            }
            if (sRef.hasOwnProperty('fieldFunc')) {
              // use the field function to derive the key and label
              // vrom the primary value
              s = sRef.fieldFunc(sv, asset);
            } else {
              // there is no translation function.  let's just use the id as
              // the key and the value
              s = [sv, sv];
            }
            if (p && s) {
              addItem(p, s);
            }
          });
        } else {
          if (p) {
            addItem(p, [null, null]);
          }
        }
      });
    });

    let extraColumns: any[] = get(opts, 'extraColumns', []);
    if (!isArray(extraColumns)) {
      extraColumns = [extraColumns];
    }

    each(extraColumns, (column) => {
      // get a ref to the column definiton
      const c: any = find(this.reportColumnOptions, {id: column});

      // iterate over everything to do the calculations at each interval and each primary/secondary
      each(primary, (primaryItem: any, itemId: string) => {
        // grouped by property, the row key is the property ID (the 'property' field's fieldFunc)
        const rowPropertyID = AssetSummaryReport.propertyRowKey(opts.primary, itemId);

        each(flatten([primaryItem]), (pItem) => {
          const items = map(pItem.items, AssetSummaryReport.eventCopy);
          // Full range for totals
          const fullRange = {
            start: intervals[0].start,
            end: intervals.length > 1 ? last(intervals).end : intervals[0].end
          };
          const updateIntervals = (theItem, intervalPropertyID?: string) => {
            if (opts.interval !== 'none') {
              each(theItem.intervals, (intervalRef, intervalKey) => {
                const items = map(intervalRef.items, AssetSummaryReport.eventCopy);

                // Extract interval index from key (e.g., 'p0' -> 0) and get specific interval range
                const intervalIndex = parseInt(intervalKey.replace('p', ''), 10);
                const specificInterval = intervals[intervalIndex];
                const intervalRange = specificInterval ? {
                  start: specificInterval.start,
                  end: specificInterval.end
                } : fullRange;

                each(items, (asset) => {
                  this.filterAsset(asset, intervalRange, opts, intervalPropertyID);
                });

                // Pass 'sort' type to get raw timestamps for date fields instead of formatted strings
                intervalRef.extras[column] = c.func(items, null, 'sort');
              });
            }
          };

          each(items, (asset) => {
            this.filterAsset(asset, fullRange, opts, rowPropertyID);
          });

          // Pass 'sort' type to get raw timestamps for date fields instead of formatted strings
          pItem.extras[column] = c.func(items, null, 'sort');

          if (opts.secondary) {
            // lets do this
            each(pItem.secondary, (sItem, sItemId) => {
              const items = map(sItem.items, AssetSummaryReport.eventCopy);
              const sPropertyID = AssetSummaryReport.propertyRowKey(opts.secondary, sItemId) ?? rowPropertyID;

              each(items, (asset) => {
                this.filterAsset(asset, fullRange, opts, sPropertyID);
              });

              sItem.extras[column] = c.func(items, null, 'sort');
              updateIntervals(sItem, sPropertyID);
            });
          } else {
            updateIntervals(pItem, rowPropertyID);
          }
        });
      });
    });

    // build an object of the accumulated data

    const r = {
      rows: opts.secondary ? primary : this.filterCategories(primary, opts),
      intervals,
      primaryKeys,
      secondaryKeys
    };

    return r;
  }

  public async getTableItems(queryParams: any): Promise<any> {
    const rows = flatten(values(get(await this.findInterval(queryParams), 'rows')));
    let currentRow = find(rows, { itemID: +queryParams.primaryID }) || filter(rows, { label: queryParams.primaryLabel }) || {};
    let assets: Asset[] = [];

    if (queryParams.secondary) {
      each(currentRow, (row) => {
        each(row?.secondary, (secondaryItem) => {
          assets.push(...secondaryItem?.items || []);
        });
      });
    } else {
      assets = currentRow?.[0]?.items || [];
    }

    // a property row's drill-down lists only that property's in-use state changes
    const rowPropertyID = AssetSummaryReport.propertyRowKey(queryParams.primary, queryParams.primaryID);
    if (rowPropertyID) {
      assets = map(assets, (asset) => ({ ...asset, stateEvents: pick(asset.stateEvents, rowPropertyID) }));
    }

    return this.assetSummaryModelService.getTableAssetDataByGraphType(assets, queryParams);
  }

  private filterAsset(asset: ReportAsset, range: { start: number; end: number; label?: string }, options: IObjectStringKeyMap<any>, rowPropertyID?: string) {
    asset.observationEvents = filter(asset?.observationEvents, (event) => {
      const observationTime = event.time * 1000;
      return observationTime >= range.start && observationTime <= range.end;
    });

    const propertyValues: { [propertyID: number]: OutputValue } = {};
    each(options, (value: string, key: string) => {
      if (includes(key, AssetSummaryReport.PropertyValuesKey)) {
        const propertyID = split(key, AssetSummaryReport.PropertyValuesKey)?.[1];

        if (propertyID) {
          propertyValues[propertyID] = value;
        }
      }
    });

    // on a property row, only that row's property: numeric readings (CVX-6510) and asset:inUse
    // time (one entity per property). Health is asset-level and stays whole.
    if (rowPropertyID) {
      asset.propertyEvents = pick(asset.propertyEvents, rowPropertyID);
      asset.stateEvents = pick(asset.stateEvents, rowPropertyID);
    }

    const anyValue = isEmpty(propertyValues);
    each(asset.propertyEvents, (events, eventPropertyID) => {
      // CVX-6545: look the property up once, not once per event
      const property = this.propertyService.getPropertyById(+eventPropertyID);
      const isAssetSubject = includes(property?.subjects, PropertySubject.Asset);
      asset.propertyEvents[eventPropertyID] = !isAssetSubject ? [] : filter(events, (event) => {
        const isPropertyValid = anyValue || some(propertyValues, (propertyAverage, propertyID) => {
          return +propertyID === property.propertyID && this.isPropertyValueValid(event, propertyAverage, property.propertyID);
        });
        const eventTime = event.time * 1000;

        return eventTime >= range.start && eventTime <= range.end && isPropertyValid;
      });
    });

    const dateRange = { startTime: range.start, endTime: range.end };
    asset.healthEntities = this.clipToRange(this.assetStatusService.buildStates(asset.healthEvents, options.timespan, dateRange, true), range);
    asset.stateEntities = this.clipToRange(this.assetStatusService.buildInUseStates(asset.stateEvents, options.timespan, dateRange, true), range);
  }

  /**
   * CVX-6545: a copy filterAsset can narrow without touching the original. filterAsset only reassigns
   * fields, except propertyEvents' entries, so that is the one container that needs its own copy.
   */
  private static eventCopy(asset: ReportAsset): ReportAsset {
    return { ...asset, propertyEvents: { ...asset.propertyEvents } };
  }

  /** The property ID a row stands for when that grouping field is 'property' (its row key), else undefined. */
  public static propertyRowKey(fieldId: string, rowKey: string): string | undefined {
    return fieldId === 'property' && +rowKey > 0 ? rowKey : undefined;
  }

  private clipToRange(entities: AssetStateHistoryEntity[], range: { start: number; end: number }): AssetStateHistoryEntity[] {
    each(entities, (entity) => {
      each(entity.stateHistoryPeriods, (statePeriod) => {
        const periods = filter(flatten([statePeriod.period]));
        statePeriod.period = filter(periods, (period: StateHistoryPeriodValue) => {
          return period.range && period.range.startTime >= range.start && period.range.endTime <= range.end;
        });
      });
    });
    return entities;
  }

  protected goToDetailPage(opts) {
    each(opts, (option, index) => {
      if (isObjectLike(option)) {
        opts[index] = JSON.stringify(option);
      }
    });
    super.goToDetailPage(opts);
  }
}
